import { CompletedCTACard, CTACard, UserSubmittedCTACard } from '../components/CTACard';
import { AccordionStep, ProgressAccordionStepProps } from '../components/AccordionStep';
import {
  HubActionData,
  ProgressStepStatusEnum,
  SimplifiedUserRole,
} from '@platform/client/schemas';
import { useState } from 'react';
import { ActionUpdateModal } from '../modal-components/ActionUpdateModal';
import cx from 'classix';
import { HubExternalLink } from '../components/HubExternalLink';

export const Action = ({
  isTeam,
  userType,
  status,
  isOpen,
  onToggle,
  planningGuideUrl,
  projectId,
  hubActionData,
  isProjectPlanMatch,
}: ProgressAccordionStepProps & {
  isProjectPlanMatch: boolean;
  planningGuideUrl?: string | null;
  projectId?: number;
  hubActionData?: HubActionData | null;
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const headerContent = getHeaderContent(userType, isTeam);
  return (
    <>
      <AccordionStep title='Action' status={status} isOpen={isOpen} onToggle={onToggle}>
        <p>📝 Your project is officially in progress!</p>
        <p>{headerContent}</p>
        <p className='text-md font-semibold'>Tips as you collaborate</p>
        <ActionList
          userType={userType}
          planningGuideUrl={planningGuideUrl}
          isTeam={isTeam}
          activeLinks={
            status === ProgressStepStatusEnum.active_in_progress ||
            status === ProgressStepStatusEnum.success
          }
          isProjectPlanMatch={isProjectPlanMatch}
        />
        <ActionCTACard
          userType={userType}
          isTeam={isTeam}
          status={status}
          hubActionData={hubActionData}
          ctaOnPress={() => setModalIsOpen(true)}
        />
      </AccordionStep>
      {projectId && (
        <ActionUpdateModal
          onClose={() => setModalIsOpen(false)}
          isOpen={modalIsOpen}
          userType={userType}
          isTeam={isTeam ?? false}
        />
      )}
    </>
  );
};

const getHeaderContent = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  if (userType === SimplifiedUserRole.PROFESSIONAL) {
    return 'Now is when your skills start creating real impact. Put your plan into action, keep in touch regularly, and use this project to apply your strengths in a new context.';
  } else {
    return `This is the stage where the work starts to make a difference. Keeping communication open helps your ${isTeam ? 'team of professionals' : 'professional'} stay aligned with your priorities and deliver outcomes that matter to your team.`;
  }
};

const ActionList = ({
  planningGuideUrl,
  activeLinks = true,
  isProjectPlanMatch = true,
}: {
  userType: SimplifiedUserRole;
  planningGuideUrl?: string | null;
  isTeam?: boolean;
  activeLinks?: boolean;
  isProjectPlanMatch?: boolean;
}) => {
  return (
    <ul className={cx('list-disc px-4', activeLinks && 'blue-links')}>
      {!isProjectPlanMatch ?
        <li>
          Refer back to your&nbsp;
          <HubExternalLink url={planningGuideUrl} active={activeLinks}>
            Planning Guide
          </HubExternalLink>
          &nbsp;throughout the project to help you prioritize and stay focused on your deliverables.
        </li>
      : <li>
          Use your Project Plan as a living reference. If priorities, timing, or scope shift once
          work begins, you can update the plan to reflect what you agree on together.
        </li>
      }
      <li>
        Schedule regular check-ins to ask questions, clarify priorities, and celebrate milestones.
      </li>
      <li>
        Provide an update here once your project is complete to move to the final step. You can also
        share an update if you need help or if something has changed.
      </li>
    </ul>
  );
};

const ActionCTACard = ({
  userType,
  isTeam,
  ctaOnPress,
  status,
  hubActionData,
}: {
  userType: SimplifiedUserRole;
  status: ProgressStepStatusEnum;
  ctaOnPress: () => void;
  isTeam?: boolean;
  hubActionData?: HubActionData | null;
}) => {
  if (
    (status === ProgressStepStatusEnum.success || status === ProgressStepStatusEnum.failure) &&
    hubActionData?.actionCompletedDate
  ) {
    return <CompletedCTACard completedDate={hubActionData.actionCompletedDate} />;
  } else if (hubActionData?.actionUpdates && hubActionData.actionUpdates.length > 0) {
    return (
      <UserSubmittedCTACard
        onPress={ctaOnPress}
        isDisabled={status !== ProgressStepStatusEnum.active_in_progress}
        latestUpdateDate={
          hubActionData.actionUpdates ? hubActionData.actionUpdates[0].createdAt : undefined
        }
      />
    );
  } else {
    return (
      <CTACard
        title='Update us on your progress'
        description={getCTAText(userType, isTeam)}
        ctaButtonProps={{
          size: 'sm',
          children: 'Provide update',
          variant: 'primary',
          onPress: ctaOnPress,
          isDisabled: status !== ProgressStepStatusEnum.active_in_progress,
        }}
      />
    );
  }
};

const getCTAText = (userType: SimplifiedUserRole, isTeam?: boolean) => {
  if (userType === SimplifiedUserRole.PROFESSIONAL) {
    return `${isTeam ? 'Anyone on your team can provide an update.' : ''} Let us know whether things are going smoothly, you're facing delays, or you've completed your deliverables.`;
  } else {
    return `Let us know whether things are going smoothly, you're facing delays, or your ${isTeam ? 'team of professionals' : 'professional'} completed your deliverables.`;
  }
};
